import { useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, MessageCircle, User, Clock, Trash2, Flag, Layers } from 'lucide-react';
import type { Post } from '../../hooks/useMuro';
import { PhotoViewer } from './PhotoViewer';

interface PostCardProps {
  post: Post;
  onLike: (id: string) => void;
  onComment: (id: string) => void;
  onDelete?: (id: string) => void;
  onReport?: (id: string) => void;
  isOwner?: boolean;
}

export function PostCard({ post, onLike, onComment, onDelete, onReport, isOwner }: PostCardProps) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const handleReport = () => {
    if (confirm('¿Deseas reportar esta publicación por contenido inapropiado? Si recibe 3 reportes será eliminada automáticamente.')) {
      onReport?.(post.id);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-700/60 backdrop-blur-md border border-white/10 rounded-[30px] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.4)] group mb-6"
    >
      {/* CABECERA: USUARIO */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-800 overflow-hidden border border-white/10">
            {post.user_avatar ? (
              <img src={post.user_avatar} alt={post.user_nombre} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-4 h-4 text-slate-500" />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-white uppercase tracking-wider">{post.user_nombre}</span>
            <span className="text-[8px] text-slate-400 font-bold flex items-center gap-1">
              <Clock className="w-2 h-2" /> {new Date(post.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        
        {isOwner ? (
          <button 
            onClick={() => onDelete?.(post.id)}
            className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center active:scale-90 transition-transform"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button 
            onClick={handleReport}
            className="w-8 h-8 rounded-full bg-white/5 text-slate-500 flex items-center justify-center active:scale-90 transition-transform hover:text-red-400"
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* IMAGEN(ES) */}
      <div 
        onClick={() => setIsViewerOpen(true)}
        className="relative h-64 bg-slate-900 overflow-hidden cursor-pointer active:opacity-90 transition-opacity"
      >
        {post.fotos && post.fotos.length > 0 ? (
          <img 
            src={post.fotos[0]} 
            alt="Muro Biker" 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-20">
            <User className="w-12 h-12" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
      </div>

      {/* CONTENIDO Y ACCIONES */}
      <div className="p-4 bg-black/40">
        <p className="text-white text-xs font-medium leading-relaxed mb-4 line-clamp-3">
          {post.descripcion}
        </p>

        <div className="flex items-center gap-4">
          <button 
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-1.5 transition-all active:scale-90 ${post.has_liked ? 'text-red-500' : 'text-slate-400'}`}
          >
            <Flame className={`w-5 h-5 ${post.has_liked ? 'fill-current' : ''}`} />
            <span className="text-[10px] font-black">{post.likes_count}</span>
          </button>

          <button 
            onClick={() => onComment(post.id)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-all active:scale-90"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-[10px] font-black">{post.comentarios_count}</span>
          </button>

          {post.fotos && post.fotos.length > 1 && (
            <div className="flex items-center gap-1 text-slate-500 ml-auto bg-white/5 px-2 py-1 rounded-lg border border-white/5">
              <Layers className="w-3 h-3" />
              <span className="text-[9px] font-black uppercase">+{post.fotos.length - 1}</span>
            </div>
          )}
        </div>
      </div>

      <PhotoViewer 
        images={post.fotos || []} 
        isOpen={isViewerOpen} 
        onClose={() => setIsViewerOpen(false)} 
      />
    </motion.div>
  );
}
